export type QualityQueue =
  | "UNQUALIFIED"
  | "QUALIFIED"
  | "CAPTURE_ISSUE"
  | "PENDING_REVIEW"
  | "TRIAL";

export type HistoryDays = 7 | 30;

export type FeedbackErrorType =
  | "DISH_RECOGNITION_WRONG"
  | "SHOULD_BE_QUALIFIED"
  | "SHOULD_BE_UNQUALIFIED"
  | "REASON_INACCURATE"
  | "STANDARD_INACCURATE"
  | "IMAGE_UNCLEAR"
  | "OTHER";

export type FeedbackDimension =
  | "CONTAINER"
  | "BROTH"
  | "INGREDIENT"
  | "PLATING"
  | "COLOR"
  | "PORTION"
  | "COOKING_STATE"
  | "CAPTURE_CONDITION"
  | "OTHER";

export type StandardIssueType =
  | "WRONG_DISH"
  | "CONTAINER"
  | "PLATING"
  | "BROTH"
  | "PORTION"
  | "OUTDATED"
  | "OTHER";

export interface QualityFeedbackPayload {
  agreed: boolean;
  note: string | null;
  errorType?: FeedbackErrorType;
  correctedDishId?: string | number;
  dishMissing?: boolean;
  disputedDimensions?: FeedbackDimension[];
  actualProblemDimensions?: FeedbackDimension[];
  standardIssueTypes?: StandardIssueType[];
}

export const queueMeta: Record<
  QualityQueue,
  { label: string; description: string; tone: "coral" | "mint" | "amber" | "blue" }
> = {
  UNQUALIFIED: { label: "原有 AI 不达标", description: "按原有 AI 结论分类", tone: "coral" },
  QUALIFIED: { label: "原有 AI 达标", description: "按原有 AI 结论分类", tone: "mint" },
  CAPTURE_ISSUE: { label: "无效图片", description: "无效或非菜图片", tone: "amber" },
  PENDING_REVIEW: { label: "有效待识别", description: "尚未人工反馈", tone: "blue" },
  TRIAL: { label: "评分试运行", description: "总分、分项与反馈", tone: "blue" },
};

export interface QualityQuery {
  date: string;
  queue: QualityQueue;
  page: number;
  pageSize: number;
  storeId?: string | number;
  dishId?: string | number;
}

export type QualityReviewQuery = Omit<QualityQuery, "page">;

export interface QualityReviewContext {
  query: QualityReviewQuery;
  pendingRecordIds: string[];
  nextPage: number;
  hasMore: boolean;
}

export function createQualityReviewContext(
  recordIds: string[],
  currentIndex: number,
  query: QualityReviewQuery,
  nextPage: number,
  hasMore: boolean
): QualityReviewContext {
  return {
    query,
    pendingRecordIds: recordIds.slice(currentIndex + 1),
    nextPage,
    hasMore,
  };
}

export function takeNextQualityRecord(context: QualityReviewContext): {
  recordId: string;
  context: QualityReviewContext;
} | null {
  const [recordId, ...pendingRecordIds] = context.pendingRecordIds;
  if (!recordId) return null;
  return {
    recordId,
    context: { ...context, pendingRecordIds },
  };
}

export function buildQualityQuery(query: QualityQuery): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
}

export function buildHistoryQuery(query: {
  days: HistoryDays;
  storeId?: string | number;
  dishId?: string | number;
}): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
}

export function formatQualifiedRate(qualified: number, evaluated: number): number {
  if (!evaluated) return 0;
  return Math.round((qualified * 1000) / evaluated) / 10;
}

export function buildFeedbackPayload(payload: QualityFeedbackPayload): QualityFeedbackPayload {
  const normalized = payload.note?.trim();
  return Object.fromEntries(
    Object.entries({ ...payload, note: normalized || null }).filter(([, value]) => {
      if (value === undefined) return false;
      return !Array.isArray(value) || value.length > 0;
    })
  ) as unknown as QualityFeedbackPayload;
}
